import type { FairBrandLogoVariant } from '@/ui/tokens/brand-logo.data'
import type { FairImagePreviewSize } from '@/ui/tokens/image-preview.data'
import type { PopularBrand } from '@/types/brands'
import balenciagaBg from './assets/brands/balenciaga-bg.webp'
import balenciagaLogo from './assets/brands/balenciaga-logo.webp'
import chanelBg from './assets/brands/chanel-bg.webp'
import chanelLogo from './assets/brands/chanel-logo.webp'
import diorBg from './assets/brands/dior-bg.webp'
import diorLogo from './assets/brands/dior-logo.webp'
import hermesBg from './assets/brands/hermes-bg.webp'
import hermesLogo from './assets/brands/hermes-logo.webp'
import lvBg from './assets/brands/lv-bg.webp'
import lvLogo from './assets/brands/lv-logo.webp'
import nikeBg from './assets/brands/nike-bg.webp'
import nikeLogo from './assets/brands/nike-logo.webp'
import pradaBg from './assets/brands/prada-bg.webp'
import pradaLogo from './assets/brands/prada-logo.webp'
import toryburchBg from './assets/brands/toryburch-bg.webp'
import toryburchLogo from './assets/brands/toryburch-logo.webp'

export const FAIR_HOME_BRANDS_DEFAULT_AUDIENCE = 'woman'

export const FAIR_HOME_BRANDS_PAGE_SIZE = 16

export const FAIR_HOME_BRANDS_AI_TIP_KEY = 'based_on_most_viewed' as const

/** Figma `1:8781` — XL cards; gap 40 */
export const FAIR_HOME_BRANDS_CARD_WIDTH_XL = 180
export const FAIR_HOME_BRANDS_CARD_GAP_XL = 40

/** Figma `1:9795` (1024) — L cards; gap 20 */
export const FAIR_HOME_BRANDS_CARD_WIDTH_L = 90
export const FAIR_HOME_BRANDS_CARD_GAP_L = 20

/** Figma `1:11713` (393) — M cards; gap 8 */
export const FAIR_HOME_BRANDS_CARD_WIDTH_M = 60
export const FAIR_HOME_BRANDS_CARD_GAP_M = 8

export interface FairHomeBrandCard {
  id: number
  name: string
  backgroundSrc: string
  logoSrc: string
  logoVariant: FairBrandLogoVariant
  isDemo?: boolean
}

interface PopularBrandFields {
  logoUrl?: string
  logo_url?: string
  background?: string
}

function getBrandLogoSrc(brand: PopularBrand & PopularBrandFields): string {
  return brand.logoUrl ?? brand.logo_url ?? ''
}

function getBrandBackgroundSrc(
  brand: PopularBrand & PopularBrandFields,
): string {
  return brand.background ?? ''
}

function mapApiBrand(
  brand: PopularBrand & PopularBrandFields,
): FairHomeBrandCard {
  return {
    id: brand.id,
    name: brand.name,
    backgroundSrc: getBrandBackgroundSrc(brand),
    logoSrc: getBrandLogoSrc(brand),
    logoVariant: 'image',
  }
}

/** Figma `1:8781` demo — used when API returns empty */
const FAIR_HOME_BRANDS_DEMO_BASE: FairHomeBrandCard[] = [
  {
    id: -1,
    name: 'Prada',
    backgroundSrc: pradaBg,
    logoSrc: pradaLogo,
    logoVariant: 'tint',
    isDemo: true,
  },
  {
    id: -2,
    name: 'Louis Vuitton',
    backgroundSrc: lvBg,
    logoSrc: lvLogo,
    logoVariant: 'tint',
    isDemo: true,
  },
  {
    id: -3,
    name: 'Dior',
    backgroundSrc: diorBg,
    logoSrc: diorLogo,
    logoVariant: 'tint',
    isDemo: true,
  },
  {
    id: -4,
    name: 'Balenciaga',
    backgroundSrc: balenciagaBg,
    logoSrc: balenciagaLogo,
    logoVariant: 'tint',
    isDemo: true,
  },
  {
    id: -5,
    name: 'Nike',
    backgroundSrc: nikeBg,
    logoSrc: nikeLogo,
    logoVariant: 'tint',
    isDemo: true,
  },
  {
    id: -6,
    name: 'Chanel',
    backgroundSrc: chanelBg,
    logoSrc: chanelLogo,
    logoVariant: 'tint',
    isDemo: true,
  },
  {
    id: -7,
    name: 'Tory Burch',
    backgroundSrc: toryburchBg,
    logoSrc: toryburchLogo,
    logoVariant: 'tint',
    isDemo: true,
  },
  {
    id: -8,
    name: 'Hermès',
    backgroundSrc: hermesBg,
    logoSrc: hermesLogo,
    logoVariant: 'tint',
    isDemo: true,
  },
]

/** 16 cards → ~2 pages at desktop (Figma shows multi-dot indicator) */
export const FAIR_HOME_BRANDS_DEMO: FairHomeBrandCard[] = [
  ...FAIR_HOME_BRANDS_DEMO_BASE,
  ...FAIR_HOME_BRANDS_DEMO_BASE.map((card, index) => ({
    ...card,
    id: -(9 + index),
  })),
]

export function resolveFairHomeBrands(
  brands: PopularBrand[] | null | undefined,
  options?: { allowDemoFallback?: boolean },
): FairHomeBrandCard[] {
  const allowDemo = options?.allowDemoFallback !== false
  const mapped = (brands ?? [])
    .map(mapApiBrand)
    .filter((card) => card.backgroundSrc && card.logoSrc)

  if (mapped.length > 0) {
    return mapped
  }

  return allowDemo ? FAIR_HOME_BRANDS_DEMO : []
}

export function getFairHomeBrandsPadInline(width: number): number {
  if (width < 768) return 16
  if (width < 1366) return 40
  if (width < 1440) return 60
  return 100
}

export function getFairHomeBrandsCardWidth(width: number): number {
  if (width < 768) return FAIR_HOME_BRANDS_CARD_WIDTH_M
  if (width < 1366) return FAIR_HOME_BRANDS_CARD_WIDTH_L
  return FAIR_HOME_BRANDS_CARD_WIDTH_XL
}

export function getFairHomeBrandsCardGap(width: number): number {
  if (width < 768) return FAIR_HOME_BRANDS_CARD_GAP_M
  if (width < 1366) return FAIR_HOME_BRANDS_CARD_GAP_L
  return FAIR_HOME_BRANDS_CARD_GAP_XL
}

export function getFairHomeBrandsPreviewSize(
  width: number,
): FairImagePreviewSize {
  if (width < 768) return 'M'
  if (width < 1366) return 'L'
  return 'XL'
}

export function getFairHomeBrandsCardsPerView(
  width: number,
  cardWidth = getFairHomeBrandsCardWidth(width),
  gap = getFairHomeBrandsCardGap(width),
): number {
  const available = width - getFairHomeBrandsPadInline(width) * 2
  const fit = Math.floor((available + gap) / (cardWidth + gap))
  return Math.max(1, fit)
}

/** Group pages — Figma dots ≈ ceil(total / cardsPerView) */
export function getFairHomeBrandsCarouselPageCount(
  totalCards: number,
  cardsPerView: number,
): number {
  if (totalCards <= 0) return 1
  return Math.max(1, Math.ceil(totalCards / cardsPerView))
}

export function clampFairHomeBrandsCarouselPage(
  page: number,
  totalPages: number,
): number {
  if (totalPages < 1) return 1
  const safe = Math.floor(page)
  if (safe < 1) return 1
  if (safe > totalPages) return totalPages
  return safe
}

export function getFairHomeBrandsCarouselOffset(
  page: number,
  cardsPerView: number,
  cardWidth: number,
  gap: number,
): number {
  return (page - 1) * cardsPerView * (cardWidth + gap)
}

export function showFairHomeBrandsNav(width: number): boolean {
  return width >= 1024
}
